import Api from "@root/components/api";

export default class MiniGameMutation {
    async publish(miniGameId) {
        let query = `mutation ($miniGameId: String!) {
            publishMiniGame(miniGameId: $miniGameId)
        }`;    
        const response = await Api.postData('/g', { 
            query: query, 
            variables: {
                miniGameId: miniGameId,
            }
        });
        return response.data.publishMiniGame;
    }

    async unpublish(miniGameId) {
        let query = `mutation ($miniGameId: String!) {
            unpublishMiniGame(miniGameId: $miniGameId)
        }`;    
        const response = await Api.postData('/g', { 
            query: query, 
            variables: {
                miniGameId: miniGameId,
            }
        });
        return response.data.unpublishMiniGame;
    }

    async delete(miniGameId) {
        let query = `mutation ($miniGameId: String!) {
            deleteMiniGame(miniGameId: $miniGameId)
        }`;    
        const response = await Api.postData('/g', { 
            query: query, 
            variables: {
                miniGameId: miniGameId,
            }
        });
        return response.data.deleteMiniGame;
    }

    async restore(miniGameId) {
        let query = `mutation ($miniGameId: String!) {
            restoreMiniGame(miniGameId: $miniGameId)
        }`;    
        const response = await Api.postData('/g', { 
            query: query, 
            variables: {
                miniGameId: miniGameId,
            }
        });
        return response.data.restoreMiniGame;
    }

    async approve(miniGameId, comment) {
        let query = `mutation ($miniGameId: String!, $comment: String!) {
            approveMiniGame(miniGameId: $miniGameId, comment: $comment)
        }`;    
        const response = await Api.postData('/g', { 
            query: query, 
            variables: {
                miniGameId: miniGameId,
                comment: comment,
            }
        });
        return response.data.approveMiniGame;
    }

    async unapprove(miniGameId, comment) {
        let query = `mutation ($miniGameId: String!, $comment: String!) {
            unapproveMiniGame(miniGameId: $miniGameId, comment: $comment)
        }`;    
        const response = await Api.postData('/g', { 
            query: query, 
            variables: {
                miniGameId: miniGameId,
                comment: comment,
            }
        });
        return response.data.unapproveMiniGame;
    }

    async create(type) {
        let query = `mutation ($gameType: String!) {
            createMiniGame(gameType: $gameType)
        }`;

        const response = await Api.postData('/g', { query: query, variables: {
            gameType: type
        }});
        return response.data?.createMiniGame;
    }    

    async addExercise(skillId) {
        let query = `mutation ($skillId: String!) {
            addExercise(skillId: $skillId)
        }`;

        const response = await Api.postData('/g', { query: query, variables: {
            skillId: skillId
        }});
        return response.data?.addExercise;
    }    

    async save(miniGame) {
        let query = `mutation ($input: MiniGameInput!) {
            saveMiniGame(input: $input)
        }`;
        return await Api.postData('/g', { query: query, variables: {
            input: {
                id: miniGame.id,
                name: miniGame.name ?? "", 
                gameType: miniGame.gameType,
                description: miniGame.description,
                background: miniGame.imageUrl,
                script: miniGame.script,
                requirements: miniGame.requirements,
                language: miniGame.language,
                style: miniGame.style,
                html: miniGame.html,
                translations: miniGame.translations,
                answerType: miniGame.answerType,
                advancedScript: miniGame.advancedScript,
                gameData: miniGame.gameData,
                primarySubjectId: miniGame.primarySubject?.id,
                primarySkillId: miniGame.primarySkill?.id,
                count: miniGame.count,
                skills: miniGame.skills.map(c=>c.id),
                hint: miniGame.hint,
                data: { 
                    [miniGame.gameType[0].toLowerCase() + miniGame.gameType.substring(1) + "Items"]: miniGame.items,
                    [miniGame.gameType[0].toLowerCase() + miniGame.gameType.substring(1) + "Info"]: (miniGame.info || {}),
                },
                time: miniGame.time ?? 30,
                fieldSets: miniGame.fieldSets,
                initCode: miniGame.initCode,
                userCode: miniGame.userCode,
                testCode: miniGame.testCode,
                mutationScript: miniGame.mutationScript,
            }
        }});
    }    

    async clone(id) {
        let query = `mutation ($id: String!) {
            cloneMiniGame(id: $id)
        }`;
        return await Api.postData('/g', { query: query, variables: {
            id: id
        }});
    }    

    async loadGameDetails(id) {
        let query = `query ($id: String!) {
            miniGame(id: $id) {
                id, name, gameUrl, gameType, autoCheck, time, 
                item(i: 0)
            }
          }`;
        const response = await Api.postData('/g', { query: query, variables: { id: id }  });
        return response.data.miniGame;
    }

    async loadGameItem(id, i) {
        let query = `query ($id: String!, $i: Int!) {
            miniGame(id: $id) {
                item(i: $i)
            }
          }`;
        const response = await Api.postData('/g', { query: query, variables: { id: id, i: i }  });
        return response.data.miniGame;
    }

    async load(subjectId) {
        let query = `query ($subjectId: String!) {
            miniGamesForSubject(subjectId: $subjectId) {
                id, name
            }
          }`;
          return await Api.postData('/g', { query: query, variables: { subjectId: subjectId }  });
    }

    async searchMiniGames(text) {
        let query = `query ($text: String!) {
            searchMiniGames(text: $text) {
                id, name, answerType, imageUrl, 
                autoCheck, count, gameType, time, language, requirements,
                authorId, authorName, authorAvatar, public, likes, canLike,
                primarySkill {
                    id, name, iknow, exercisePoints,
                    primarySubject { id, name }
                },
                authorId, authorName,
                skills { id, name, iknow }
            }
        }`;
        let result = await Api.postData('/g', { query: query, variables: {
            text: text,
        } });
          return result.data.searchMiniGames;
    }

    async searchMiniGamesInSubject(subjectId, text, offset, count) {
        let query = `query ($subjectId: String, $text: String!, $offset: Int!, $count: Int!) {
            searchMiniGamesInSubject(subjectId: $subjectId, text: $text, offset: $offset, count: $count) {
                id, name, answerType, imageUrl, 
                autoCheck, count, gameType, time, language, requirements,
                authorId, authorName, authorAvatar, public, likes, canLike,
                items info
                primarySkill {
                    id, name, iknow, exercisePoints,
                    primarySubject { id, name }
                },
                authorId, authorName,
                skills { id, name, iknow }
            }
        }`;
        let result = await Api.postData('/g', { query: query, variables: {
            subjectId: subjectId,
            text: text,
            offset: offset,
            count: count,
        }});
        return result.data.searchMiniGamesInSubject;
    }
}